import { PropsWithChildren } from 'react';

import { Button, Icons, notify, Typography, Stack } from '@cast/design-system';
import { useMarkSalesContactedMutation } from '@cast/shared-trial-data-access';
import { useInitializeTrialDialog, useTrial } from '@cast/shared-trial-ui';

import { openScheduleDialog } from '../../core/integrations';
import {
  OverlayWithBlur,
  OverlayWithBlurProps,
} from '../overlays/OverlayWithBlur';

type Props = PropsWithChildren<{
  position?: OverlayWithBlurProps['position'];
  heading: string;
  description: string;
}>;

export const TrialOverlay = ({
  heading,
  description,
  position,
  children,
}: Props) => {
  const { openDialog: openInitializeTrialDialog } = useInitializeTrialDialog();
  const { mutateAsync: markSalesContacted } = useMarkSalesContactedMutation();

  const { state, isPreTrial } = useTrial();

  const handleCTA = () => {
    if (isPreTrial) {
      openInitializeTrialDialog();
      return;
    }
    openScheduleDialog({
      onMeetingBooked: () => {
        markSalesContacted().then(() => {
          notify.success('Meeting scheduled');
        });
      },
      onError: () =>
        notify.error(
          'Failed to load the booking form. Please try again later.'
        ),
    });
  };

  return (
    <OverlayWithBlur
      content={
        <Stack
          position="absolute"
          sx={{
            top: `calc(50% + ${position?.top ? position.top / 2 : 0}px)`,
            left: 0,
            right: 0,
            transform: 'translateY(-50%)',
            margin: '0 auto',
            width: '80%',
            maxWidth: '800px',
          }}
          alignItems="center"
          zIndex={2}
          gap={6}
        >
          <Typography variant="A1">{heading}</Typography>
          <Typography variant="P12R" color="textSecondary" textAlign="center">
            {description}
          </Typography>
          <Button
            variant="text"
            endIcon={
              state !== 'active' ? <Icons.ArrowRight /> : <Icons.Calendar />
            }
            sx={{ mt: 10 }}
            onClick={handleCTA}
            testId="trial-overlay-button"
          >
            {state !== 'active'
              ? 'Experience the unlimited for free'
              : 'Talk to us to upgrade'}
          </Button>
        </Stack>
      }
      testId="trial-overlay"
      position={position}
      active
    >
      {children}
    </OverlayWithBlur>
  );
};
